import { useI18n } from '../../../i18n.js';
import { Icon } from '../../Icon.js';
import { VoiceChatBar } from '../../VoiceChatBar.js';

interface MuushigControlsProps {
  isMyTurn: boolean;
  isPaused: boolean;
  stage: 'decision' | 'trump-exchange' | 'exchange' | 'playing';
  canFold: boolean;
  selectedCount: number;
  stockCount?: number;
  playError: string | null;
  stagePrompt?: string | null;
  onPass: () => void;
  onPlay: () => void;
  onClearSelection?: () => void;
  isFinished?: boolean;
  isHost?: boolean;
  matchOver?: boolean;
  onPlayAgain?: () => void;
  onRestartMatch?: () => void;
  onReturnToLobby?: () => void;
}

export function MuushigControls({
  isMyTurn,
  isPaused,
  stage,
  canFold,
  selectedCount,
  stockCount = 0,
  playError,
  stagePrompt,
  onPass,
  onPlay,
  onClearSelection,
  isFinished = false,
  isHost = false,
  matchOver = false,
  onPlayAgain,
  onRestartMatch,
  onReturnToLobby,
}: MuushigControlsProps) {
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';

  if (isFinished) {
    return (
      <div className="player-controls muushig-controls-wrapper">
        <div className="player-controls-secondary">
          <VoiceChatBar compact />
        </div>

        <div className="player-controls-primary">
          {isHost ? (
            <>
              {!matchOver && onPlayAgain && (
                <button
                  type="button"
                  className="play-action is-suggested"
                  onClick={onPlayAgain}
                  title={isMn ? 'Дараагийн үе эхлүүлэх' : 'Start Next Round'}
                >
                  <Icon name="play" />
                  <span>{isMn ? 'Дараагийн үе' : 'Next Round'}</span>
                </button>
              )}
              {onRestartMatch && (
                <button
                  type="button"
                  className={`play-action${matchOver ? ' is-suggested' : ''}`}
                  onClick={onRestartMatch}
                  style={!matchOver ? { background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.25)' } : undefined}
                  title={isMn ? 'Шинэ тэмцээн эхлүүлэх (Оноог 15 болгоно)' : 'Start New Match (Reset scores to 15)'}
                >
                  <Icon name="rotate-ccw" />
                  <span>{isMn ? 'Шинэ тэмцээн' : 'New Match'}</span>
                </button>
              )}
              {onReturnToLobby && (
                <button
                  type="button"
                  className="pass-action"
                  onClick={onReturnToLobby}
                  title={isMn ? 'Лобби руу буцах' : 'Return to Lobby'}
                >
                  <Icon name="log-out" />
                  <span>{isMn ? 'Лобби' : 'Lobby'}</span>
                </button>
              )}
            </>
          ) : (
            <>
              <div
                style={{
                  color: '#fde68a',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  padding: '6px 12px',
                  background: 'rgba(0, 0, 0, 0.35)',
                  borderRadius: 8,
                }}
              >
                {isMn ? 'Эзэн эхлүүлэхийг хүлээж байна...' : 'Waiting for host to start...'}
              </div>
              {onReturnToLobby && (
                <button
                  type="button"
                  className="pass-action"
                  onClick={onReturnToLobby}
                  title={isMn ? 'Лобби руу буцах' : 'Return to Lobby'}
                >
                  <Icon name="log-out" />
                  <span>{isMn ? 'Лобби' : 'Lobby'}</span>
                </button>
              )}
            </>
          )}
        </div>

        <div className="player-controls-spacer" />
      </div>
    );
  }

  const isTrumpExchange = stage === 'trump-exchange';
  const isStockExchange = stage === 'exchange';

  const playLabel =
    stage === 'decision'
      ? isMn ? 'Орох (Тоглох)' : 'Stay in Game'
      : isTrumpExchange
      ? selectedCount === 1
        ? isMn ? 'Навших' : 'Take Pot Card'
        : isMn ? 'Хэвээр үлдээх' : 'Keep Hand'
      : isStockExchange
      ? selectedCount > 0
        ? isMn ? `Солих (${selectedCount})` : `Swap (${selectedCount})`
        : isMn ? 'Солихгүй (Үлдээх)' : 'Keep Hand'
      : t.playButton;

  const playIcon =
    (isTrumpExchange && selectedCount === 1) || (isStockExchange && selectedCount > 0)
      ? 'arrow-left-right'
      : stage === 'decision'
      ? 'check'
      : isStockExchange || (isTrumpExchange && selectedCount === 0)
      ? 'check'
      : 'arrow-up';

  const isDisabled =
    !isMyTurn ||
    !!playError ||
    (isTrumpExchange && selectedCount > 1) ||
    isPaused;

  const cannotFoldReason = !canFold
    ? isMn
      ? 'Дор хаяж 2 тоглогч тоглох ёстой тул өнжих боломжгүй'
      : 'At least 2 players must stay in the round'
    : undefined;

  const playTooltip =
    isStockExchange && selectedCount === 0 && stockCount > 0
      ? isMn ? `Газарт ${stockCount} мод үлдсэн (Хэвээр үлдээх бол дарна уу)` : `${stockCount} cards left in stock (Click to keep hand)`
      : playError ?? playLabel;

  return (
    <div className="player-controls muushig-controls-wrapper">
      {/* Centered stage prompt banner floating right above the action buttons (no impact on button layout) */}
      {stagePrompt && (
        <div className="muushig-stage-banner-centered" role="status">
          <span>{stagePrompt}</span>
        </div>
      )}

      <div className="player-controls-secondary">
        <VoiceChatBar compact />
      </div>

      <div className="player-controls-primary">
        {/* Stage 1: Decision - Clear Stay vs Sit Out options */}
        {stage === 'decision' && (
          <button
            className="pass-action muushig-fold-action"
            onClick={onPass}
            disabled={!isMyTurn || !canFold || isPaused}
            title={cannotFoldReason ?? (isMn ? 'Энэ өрөгт өнжих (0 оноо)' : 'Sit out this round (0 pts)')}
            data-tooltip={cannotFoldReason}
            aria-label={isMn ? 'Өнжих' : 'Sit out'}
          >
            <Icon name="skip-forward" />
            <span>{isMn ? 'Өнжих' : 'Sit out'}</span>
          </button>
        )}

        {/* Clear selection shortcut during exchange */}
        {(isStockExchange || isTrumpExchange) && selectedCount > 0 && onClearSelection && isMyTurn && (
          <button
            type="button"
            className="secondary-action muushig-clear-btn"
            onClick={onClearSelection}
            title={isMn ? 'Сонголт цэвэрлэх' : 'Clear selection'}
            aria-label={isMn ? 'Сонголт цэвэрлэх' : 'Clear selection'}
          >
            <Icon name="rotate-ccw" size={14} />
            <span>{isMn ? 'Цэвэрлэх' : 'Clear'}</span>
          </button>
        )}

        <button
          className={`play-action${stage === 'decision' ? ' muushig-join-action' : ''}`}
          onClick={onPlay}
          disabled={isDisabled}
          title={playTooltip}
        >
          <Icon name={playIcon} />
          <span>{playLabel}</span>
        </button>
      </div>

      <div className="player-controls-spacer" />
    </div>
  );
}
